import { useEffect, useState, useCallback } from "react";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Eye, EyeOff, Loader2, Save } from "lucide-react";

export default function Services() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit: 50 });
    if (platform) qs.set("platform", platform);
    if (search) qs.set("search", search);
    api.get(`/admin/services?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [platform, search, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)).catch(() => {}); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/services/sync"); toast.success(`Senkronizasyon: ${data.inserted} yeni, ${data.updated} güncellendi`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Senkronizasyon başarısız"); }
    finally { setSyncing(false); }
  };

  const toggle = async (s, field) => {
    try { await api.patch(`/admin/services/${s.service_id}`, { [field]: !s[field] }); load(); }
    catch { toast.error("Hata"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/services/${edit.service_id}`, {
        name_tr: edit.name_tr, markup_percent: edit.markup_percent === "" ? null : Number(edit.markup_percent),
        fixed_price: edit.fixed_price === "" ? null : Number(edit.fixed_price), order: Number(edit.order || 0),
        description: edit.description,
      });
      toast.success("Servis güncellendi"); setEdit(null); load();
    } catch { toast.error("Hata"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-extrabold text-white">Servisler <span className="text-sm text-gray-400 font-normal">({data.total})</span></h1>
        <button onClick={sync} disabled={syncing} className="btn-gradient px-5 py-2.5 rounded-xl font-bold flex items-center gap-2" data-testid="sync-services-btn">
          {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Servisleri Senkronize Et
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-[#171922] border border-white/10 rounded-xl px-4 flex-1 min-w-[200px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Servis ara..." className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" data-testid="services-search" />
        </div>
        <select value={platform} onChange={(e) => { setPlatform(e.target.value); setPage(1); }} className="bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm" data-testid="services-platform-filter">
          <option value="">Tüm Platformlar</option>
          {platforms.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.count})</option>)}
        </select>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-gray-400 text-left border-b border-white/10">
            {["ID", "Servis Adı", "Platform", "Min-Max", "Alış(1000)", "Satış(1000)", "Durum", "İşlem"].map((h) => <th key={h} className="px-3 py-3 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.service_id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-3 py-3 text-gray-500 text-xs">{s.service_id}</td>
                <td className="px-3 py-3 text-white max-w-[280px]"><div className="line-clamp-2" title={s.name_tr || s.name}>{s.name_tr || s.name}</div></td>
                <td className="px-3 py-3 text-gray-400 whitespace-nowrap">{s.platform_label}</td>
                <td className="px-3 py-3 text-gray-400 whitespace-nowrap text-xs">{s.min}-{s.max}</td>
                <td className="px-3 py-3 text-gray-400 whitespace-nowrap text-xs">{s.provider_rate}</td>
                <td className="px-3 py-3 text-white whitespace-nowrap text-xs">{s.fixed_price != null ? formatTL(s.fixed_price) : (s.markup_percent != null ? `+%${s.markup_percent}` : "-")}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => toggle(s, "active")} className={`px-2 py-1 rounded text-xs font-semibold ${s.active ? "bg-green-500/15 text-green-400" : "bg-gray-500/15 text-gray-400"}`} data-testid={`toggle-active-${s.service_id}`}>{s.active ? "Aktif" : "Pasif"}</button>
                    <button onClick={() => toggle(s, "hidden")} className="p-1.5 rounded bg-white/5 text-gray-300" title={s.hidden ? "Gizli" : "Görünür"}>{s.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  </div>
                </td>
                <td className="px-3 py-3"><button onClick={() => setEdit({ ...s, markup_percent: s.markup_percent ?? "", fixed_price: s.fixed_price ?? "" })} className="text-[#d8b4fe] text-xs font-semibold hover:underline" data-testid={`edit-service-${s.service_id}`}>Düzenle</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && (
        <div className="flex justify-center gap-2 mt-4 flex-wrap">
          {Array.from({ length: Math.min(data.pages, 15) }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} className={`w-9 h-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-white text-[#1f222d]" : "bg-white/5 text-gray-300"}`}>{i + 1}</button>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setEdit(null)}>
          <div className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="service-edit-modal">
            <h3 className="font-bold text-white mb-4">Servis Düzenle</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-gray-400">Servis Adı (TR)</label><input value={edit.name_tr || ""} onChange={(e) => setEdit({ ...edit, name_tr: e.target.value })} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
              <div><label className="text-xs text-gray-400">Açıklama</label><textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={2} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-400">Kâr Yüzdesi (%)</label><input type="number" value={edit.markup_percent} onChange={(e) => setEdit({ ...edit, markup_percent: e.target.value })} placeholder="Global" className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
                <div><label className="text-xs text-gray-400">Sabit Fiyat (1000)</label><input type="number" value={edit.fixed_price} onChange={(e) => setEdit({ ...edit, fixed_price: e.target.value })} placeholder="Opsiyonel" className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
              </div>
              <div><label className="text-xs text-gray-400">Sıralama</label><input type="number" value={edit.order || 0} onChange={(e) => setEdit({ ...edit, order: e.target.value })} className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mt-1" /></div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEdit(null)} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white font-semibold">İptal</button>
              <button onClick={saveEdit} className="btn-gradient flex-1 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2" data-testid="save-service-btn"><Save size={16} /> Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
